import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown, CornerDownLeft } from "lucide-react";
import type { ConversionData } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";
import { CLOSED_MATCH_STATUSES } from "@/utils/matches/status";
import { TransitionMatchesModal } from "./TransitionMatchesModal";

interface ConversionTableProps {
	conversions: ConversionData[];
	dropOffs: ConversionData[];
	tooltip?: string;
	dateFrom?: string | null;
	dateTo?: string | null;
	userId?: string | null;
}

const STAGE_ORDER = ["applied", "matched", "screening", "sent", "interview", "offer", "hired"];

const STAGE_COLORS: Record<string, string> = {
	applied: "#3b82f6",
	matched: "#6366f1",
	screening: "#0ea5e9",
	sent: "#8b5cf6",
	interview: "#14b8a6",
	offer: "#f59e0b",
	hired: "#22c55e",
};

type GroupDirection = "from" | "to";

interface TransitionEntry {
	label: string;
	fromStage: string;
	toStage: string;
	count: number;
	pct: number;
}

interface StageGroup {
	stage: string;
	totalOut: number;
	progressions: TransitionEntry[];
	regressions: TransitionEntry[];
	dropOffs: TransitionEntry[];
}

export function ConversionTable({ conversions, dropOffs, tooltip, dateFrom, dateTo, userId }: ConversionTableProps) {
	const { t } = useTranslation();
	const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
	const [modalTransition, setModalTransition] = useState<{ from: string; to: string } | null>(null);
	const [direction, setDirection] = useState<GroupDirection>("to");

	const grouped = useMemo(() => {
		const allTransitions = [...conversions, ...dropOffs];
		const byStage = new Map<string, { progressions: ConversionData[]; regressions: ConversionData[]; drops: ConversionData[]; total: number }>();

		allTransitions.forEach(tr => {
			const groupKey = direction === "from" ? tr.from_stage : tr.to_stage;

			if (!byStage.has(groupKey))
				byStage.set(groupKey, { progressions: [], regressions: [], drops: [], total: 0 });

			const group = byStage.get(groupKey)!;
			group.total += tr.count;

			// Classification is always based on actual transition direction (from → to)
			if (CLOSED_MATCH_STATUSES.includes(tr.to_stage as any)) {
				group.drops.push(tr);
			} else {
				const fromIdx = STAGE_ORDER.indexOf(tr.from_stage);
				const toIdx = STAGE_ORDER.indexOf(tr.to_stage);
				if (toIdx > fromIdx || CLOSED_MATCH_STATUSES.includes(tr.from_stage as any))
					group.progressions.push(tr);
				else
					group.regressions.push(tr);
			}
		});

		const result: StageGroup[] = [];

		const sortedStages = Array.from(byStage.keys()).sort((a, b) => {
			const ai = STAGE_ORDER.indexOf(a);
			const bi = STAGE_ORDER.indexOf(b);
			return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
		});

		sortedStages.forEach(stage => {
			const data = byStage.get(stage)!;
			if (data.total === 0) return;

			const mapEntry = (p: ConversionData): TransitionEntry => ({
				label: direction === "from" ? p.to_stage : p.from_stage,
				fromStage: p.from_stage,
				toStage: p.to_stage,
				count: p.count,
				pct: Math.round((p.count / data.total) * 100),
			});

			result.push({
				stage,
				totalOut: data.total,
				progressions: data.progressions.map(mapEntry).sort((a, b) => b.count - a.count),
				regressions: data.regressions.map(mapEntry).sort((a, b) => b.count - a.count),
				dropOffs: data.drops.map(mapEntry).sort((a, b) => b.count - a.count),
			});
		});

		return result;
	}, [conversions, dropOffs, direction]);

	const hasData = grouped.length > 0;

	const toggleStage = (stage: string) => {
		setExpandedStages(prev => {
			const next = new Set(prev);
			if (next.has(stage))
				next.delete(stage);
			else
				next.add(stage);
			return next;
		});
	};

	const arrowBefore = direction === "from";

	return (
		<Card>
			<CardHeader className={!hasData ? "pb-2" : undefined}>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<CardTitle className="text-lg">{t("dashboard.kpi.conversionRates")}</CardTitle>
						{tooltip && <KPITooltip text={tooltip} />}
					</div>
					{hasData && (
						<div className="flex items-center rounded-lg border bg-muted/30 p-0.5">
							<button
								onClick={() => { setDirection("to"); setExpandedStages(new Set()); }}
								className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
									direction === "to"
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								{t("dashboard.kpi.transitionTo")}
							</button>
							<button
								onClick={() => { setDirection("from"); setExpandedStages(new Set()); }}
								className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
									direction === "from"
										? "bg-background text-foreground shadow-sm"
										: "text-muted-foreground hover:text-foreground"
								}`}
							>
								{t("dashboard.kpi.transitionFrom")}
							</button>
						</div>
					)}
				</div>
			</CardHeader>
			<CardContent>
				{!hasData ? (
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				) : (
					<div className="space-y-1">
						{grouped.map(group => {
							const isExpanded = expandedStages.has(group.stage);
							const progressionTotal = group.progressions.reduce((s, p) => s + p.count, 0);
							const regressionTotal = group.regressions.reduce((s, r) => s + r.count, 0);
							const dropTotal = group.dropOffs.reduce((s, d) => s + d.count, 0);
							const progressionPct = Math.round((progressionTotal / group.totalOut) * 100);
							const regressionPct = Math.round((regressionTotal / group.totalOut) * 100);
							const dropPct = Math.round((dropTotal / group.totalOut) * 100);
							const stageColor = STAGE_COLORS[group.stage] || "#94a3b8";

							return (
								<div key={group.stage} className="rounded-lg border overflow-hidden">
									{/* Stage header — always visible */}
									<button
										onClick={() => toggleStage(group.stage)}
										className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left"
									>
										{isExpanded
											? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
											: <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}

										{/* Stage name with color dot */}
										<div className="flex items-center gap-2 min-w-[100px]">
											<div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: stageColor }} />
											<span className="font-medium text-sm">
												{t(`dashboard.kpi.stages.${group.stage}`, group.stage)}
											</span>
										</div>

										{/* Summary bar: green = progression, orange = regression, red = drop-off */}
										<div className="flex-1 flex items-center gap-2">
											<div className="flex-1 h-3 bg-muted rounded-full overflow-hidden flex">
												{progressionPct > 0 && (
													<div
														className="h-full bg-green-500 transition-all"
														style={{ width: `${progressionPct}%` }}
													/>
												)}
												{regressionPct > 0 && (
													<div
														className="h-full bg-amber-400 transition-all"
														style={{ width: `${regressionPct}%` }}
													/>
												)}
												{dropPct > 0 && (
													<div
														className="h-full bg-red-400 transition-all"
														style={{ width: `${dropPct}%` }}
													/>
												)}
											</div>
										</div>

										{/* Quick stats */}
										<div className="flex items-center gap-2 text-xs shrink-0">
											<span className="flex items-center gap-0.5 text-green-600">
												<TrendingUp className="h-3 w-3" />
												{progressionPct}%
											</span>
											{regressionPct > 0 && (
												<span className="flex items-center gap-0.5 text-amber-500">
													<CornerDownLeft className="h-3 w-3" />
													{regressionPct}%
												</span>
											)}
											<span className="flex items-center gap-0.5 text-red-500">
												<TrendingDown className="h-3 w-3" />
												{dropPct}%
											</span>
											<span className="text-muted-foreground w-8 text-right">{group.totalOut}</span>
										</div>
									</button>

									{/* Expanded detail */}
									{isExpanded && (
										<div className="px-3 pb-3 pt-1 border-t bg-muted/20 space-y-2">
											{/* Progressions */}
											{group.progressions.length > 0 && (
												<div>
													<div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
														<TrendingUp className="h-3 w-3 text-green-600" />
														{t("dashboard.kpi.progressions")}
													</div>
													<div className="space-y-1.5">
														{group.progressions.map((p, i) => (
															<div
																key={i}
																className="flex items-center gap-2 pl-4 cursor-pointer hover:bg-muted/40 rounded -mx-1 px-1 py-0.5"
																onClick={() => setModalTransition({ from: p.fromStage, to: p.toStage })}
															>
																<span className="text-xs min-w-[120px]">
																	{arrowBefore ? "→ " : ""}{t(`dashboard.kpi.stages.${p.label}`, p.label)}{arrowBefore ? "" : " →"}
																</span>
																<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
																	<div
																		className="h-full bg-green-500 rounded-full"
																		style={{ width: `${p.pct}%` }}
																	/>
																</div>
																<span className="text-xs font-medium text-green-600 w-10 text-right">{p.pct}%</span>
																<span className="text-xs text-muted-foreground w-6 text-right">{p.count}</span>
															</div>
														))}
													</div>
												</div>
											)}

											{/* Regressions */}
											{group.regressions.length > 0 && (
												<div>
													<div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
														<CornerDownLeft className="h-3 w-3 text-amber-500" />
														{t("dashboard.kpi.regressions")}
													</div>
													<div className="space-y-1.5">
														{group.regressions.map((r, i) => (
															<div
																key={i}
																className="flex items-center gap-2 pl-4 cursor-pointer hover:bg-muted/40 rounded -mx-1 px-1 py-0.5"
																onClick={() => setModalTransition({ from: r.fromStage, to: r.toStage })}
															>
																<span className="text-xs min-w-[120px]">
																	{arrowBefore ? "→ " : ""}{t(`dashboard.kpi.stages.${r.label}`, r.label)}{arrowBefore ? "" : " →"}
																</span>
																<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
																	<div
																		className="h-full bg-amber-400 rounded-full"
																		style={{ width: `${r.pct}%` }}
																	/>
																</div>
																<span className="text-xs font-medium text-amber-500 w-10 text-right">{r.pct}%</span>
																<span className="text-xs text-muted-foreground w-6 text-right">{r.count}</span>
															</div>
														))}
													</div>
												</div>
											)}

											{/* Drop-offs */}
											{group.dropOffs.length > 0 && (
												<div>
													<div className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
														<TrendingDown className="h-3 w-3 text-red-500" />
														{t("dashboard.kpi.dropOffs")}
													</div>
													<div className="space-y-1.5">
														{group.dropOffs.map((d, i) => (
															<div
																key={i}
																className="flex items-center gap-2 pl-4 cursor-pointer hover:bg-muted/40 rounded -mx-1 px-1 py-0.5"
																onClick={() => setModalTransition({ from: d.fromStage, to: d.toStage })}
															>
																<span className="text-xs min-w-[120px]">
																	{arrowBefore ? "→ " : ""}{t(`dashboard.kpi.stages.${d.label}`, d.label)}{arrowBefore ? "" : " →"}
																</span>
																<div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
																	<div
																		className="h-full bg-red-400 rounded-full"
																		style={{ width: `${d.pct}%` }}
																	/>
																</div>
																<span className="text-xs font-medium text-red-500 w-10 text-right">{d.pct}%</span>
																<span className="text-xs text-muted-foreground w-6 text-right">{d.count}</span>
															</div>
														))}
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</CardContent>

			<TransitionMatchesModal
				open={modalTransition !== null}
				onClose={() => setModalTransition(null)}
				fromStage={modalTransition?.from ?? ""}
				toStage={modalTransition?.to ?? ""}
				dateFrom={dateFrom ?? null}
				dateTo={dateTo ?? null}
				userId={userId ?? null}
			/>
		</Card>
	);
}
